import type { QCSeverity } from '@ohear/shared'
/**
 * Supervisor-side API surface.
 *
 * The actor-side `takes.ts` owns the WRITE flow (presign, upload, finalize).
 * This module handles the supervisor's side: project list, project overview
 * grid, the pending-decision queue, and the per-take decision endpoints.
 *
 * Endpoints follow PRD §10.4 (review) and the schema in `lib/api/types.ts`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api'

export interface SupervisorProjectSummary {
  project_id: string
  project_code: string
  project_name: string
  organization_id: string
  organization_name: string
  total_cues: number
  approved: number
  pending_supervisor: number
  pending_pickup: number
  /** ISO-8601 of the last delivered take. Null if none yet. */
  last_take_at: string | null
}

export interface CharacterBatchCell {
  batch_id: string
  batch_number: number
  approved: number
  pending_supervisor: number
  pending_pickup: number
  total: number
}

export interface SupervisorCharacterRow {
  character_id: string
  character_code: string
  character_display_name: string
  actor_display_name: string | null
  approved: number
  total: number
  pending_pickups: number
  latest_stem_version: number | null
  /** Per-batch counts, ordered by batch_number ascending. */
  batches: readonly CharacterBatchCell[]
}

export interface ProjectOverview {
  project_id: string
  project_name: string
  organization_name: string
  total_cues: number
  approved: number
  pending_supervisor: number
  pending_pickup: number
  characters: readonly SupervisorCharacterRow[]
}

export interface TakeQcSummary {
  /** Stable QC check id, e.g. "noise_floor". */
  check_id: string
  severity: QCSeverity
  friendly_message: string | null
}

export interface TakeReference {
  /** Signed URL to the AI reference voice for this cue. */
  url: string
  duration_sec: number
}

export interface PendingTake {
  take_id: string
  cue_id: string
  cue_number: number
  cue_text: string
  character_id: string
  character_code: string
  character_display_name: string
  actor_display_name: string | null
  /** Take version: 1 = original, 2+ = pickup re-records. */
  version: number
  /** ISO-8601. */
  submitted_at: string
  duration_sec: number
  /** Signed URL to the take's WAV (or FLAC for transit). */
  audio_url: string
  /** Pre-rendered mini SVG `path d` for the row thumbnail (80px tall). */
  waveform_path_d: string | null
  /** ViewBox width the path was generated against. */
  waveform_path_width: number | null
  /** Reference voice for A/B comparison. */
  reference: TakeReference | null
  qc: readonly TakeQcSummary[]
}

export interface PendingTakesPage {
  takes: readonly PendingTake[]
  /** Total pending count across the project (the page may be truncated). */
  total_pending: number
}

export type TakeDecision = 'approved' | 'rejected_pickup' | 'archived'

export interface DecisionPayload {
  decision: TakeDecision
  supervisor_note?: string | null
}

const projectsKey = ['supervisor', 'projects'] as const
const overviewKey = (projectId: string) => ['supervisor', 'overview', projectId] as const
const queueKey = (projectId: string) => ['supervisor', 'queue', projectId] as const

/** Projects this supervisor reviews — across all orgs. */
export function useSupervisorProjects() {
  return useQuery({
    queryKey: projectsKey,
    queryFn: () =>
      api<{ projects: readonly SupervisorProjectSummary[] }>('/api/v1/supervisor/projects'),
  })
}

/** Project-level grid + completion metrics. */
export function useProjectOverview(projectId: string) {
  return useQuery({
    queryKey: overviewKey(projectId),
    queryFn: () => api<ProjectOverview>(`/api/v1/projects/${projectId}/overview`),
    enabled: projectId.length > 0,
  })
}

/** Sorted queue of takes awaiting a supervisor decision. */
export function useReviewQueue(projectId: string) {
  return useQuery({
    queryKey: queueKey(projectId),
    queryFn: () => api<PendingTakesPage>(`/api/v1/projects/${projectId}/takes/pending`),
    enabled: projectId.length > 0,
  })
}

/** Approve / pickup / archive a single take. Hits the unified /decide endpoint. */
export function useDecideTake(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ takeId, ...body }: { takeId: string } & DecisionPayload) =>
      api<{ ok: true; decision: string }>(`/api/v1/takes/${takeId}/decide`, {
        method: 'POST',
        body: JSON.stringify({
          decision: body.decision,
          supervisor_note: body.supervisor_note ?? null,
        }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queueKey(projectId) })
      void qc.invalidateQueries({ queryKey: overviewKey(projectId) })
    },
  })
}

/** Approve every take in the supplied list in one server call. */
export function useBulkApproveTakes(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (takeIds: readonly string[]) =>
      api<{ approved: number }>(`/api/v1/projects/${projectId}/takes/bulk-approve`, {
        method: 'POST',
        body: JSON.stringify({ take_ids: takeIds }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queueKey(projectId) })
      void qc.invalidateQueries({ queryKey: overviewKey(projectId) })
    },
  })
}
